// Sentinel hosts Poiesis's rendering (Work screen, 2026-10-21): the list and
// the record page are Poiesis's components (`@poiesis/*`), one engine for
// both apps. This module is the one place Sentinel wires them in:
// - Poiesis requests ride SENTINEL's NomOS session (configureHttp), never a
//   Poiesis login of their own;
// - every cell kind is registered (Poiesis's own entry point does the same);
// - one react-query client serves the Poiesis subtree (Sentinel's own pages
//   do not use react-query).
import type { ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { configureHttp } from '@poiesis/api/http'
import '@poiesis/widgets/cells'
import { registerAgentMentions } from '@poiesis/records/agentMention'
import { registerRichText } from '@poiesis/widgets/richText'
import { LazyMarkdown } from '@/components/common/LazyMarkdown'
import { activeTenantSlug, nomosRoot, registerTenantCache } from '@/services/nomos/client'
import { api } from '@/services/api'

function nomosToken(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('nomos_token') : null
  } catch {
    return null
  }
}

// slug: a cell widget that names records (a member's access filter) asks the active tenant's chips
configureHttp({ token: nomosToken, baseUrl: nomosRoot() ?? '', slug: activeTenantSlug })
// @<agent> in a record's comments runs the tenant's agent (the person's comment, a durable run)
registerAgentMentions()
// the agent's answers in comments render as Markdown (owner, 2026-10-23: raw ** and - showed)
registerRichText((text) => <LazyMarkdown>{text}</LazyMarkdown>)

export const poiesisQueryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: true, staleTime: 5_000, retry: 1 } },
})

/** Everything Poiesis read belongs to ONE login in ONE tenant: a sign-in, a
 *  sign-out or a tenant switch drops it all (the same moments Sentinel's own
 *  NomOS caches reset), so the next person never sees the last one's rows. */
export function clearPoiesisCache(): void {
  poiesisQueryClient.cancelQueries()
  poiesisQueryClient.clear()
}
api.onSessionChange(clearPoiesisCache)
registerTenantCache(clearPoiesisCache)

export function PoiesisHost({ children, client = poiesisQueryClient }: { children: ReactNode; client?: QueryClient }) {
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
